import { pacificToday } from '../../../shared/dates';
import type { TouchChannel } from '../../../shared/enums';
import { TOUCH_CHANNELS } from '../../../shared/enums';
import type { ExportProfile } from '../../../shared/export';
import { shell } from '../app';
import { loadExportProfile } from '../data/activity';
import { copyText } from '../data/drafts';
import { getSnapshot, onStoreChange } from '../data/store';
import { filterTouches, touchSummary, touchesBlock, touchesCsv, touchesOf } from '../logic/activity';
import type { Touch } from '../logic/activity';
import { addDays, dayHeading, formatDate, todayLocal } from '../logic/dates';
import { btn, empty, linkBtn, row, section } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { exportFile } from '../ui/download';
import { CHANNEL_LABELS, OUTCOME_LABELS, PLAYBOOK_VARIANT_LABELS, SPOKE_WITH_LABELS } from '../ui/labels';
import type { View } from '../ui/router';


/**
 * The activity log (D90): every touch and how it went, and the end-of-day transfer to Salesforce
 * (D91) as a block to paste or a CSV to import. Read from the mirror, so it works with no signal.
 * Nothing here writes to Salesforce — the app has no connection to it and never will.
 */

type Span = 'today' | '7' | '30' | 'all';

const SPANS: { value: Span; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: 'all', label: 'All' },
];

const fromFor = (span: Span, today: string): string | undefined =>
  span === 'today' ? today : span === 'all' ? undefined : addDays(today, -(Number(span) - 1));

const touchRow = (t: Touch) => {
  const { visit, lead } = t;
  const channel = visit.channel ?? 'walk_in';
  return row({
    title: [
      span('tag tag--visited', CHANNEL_LABELS[channel]),
      ' ',
      span('row__name', lead?.business_name ?? '(deleted lead)'),
    ],
    meta: [
      formatDate(pacificToday(new Date(visit.visited_at))),
      ` · ${SPOKE_WITH_LABELS[visit.spoke_with]} · ${OUTCOME_LABELS[visit.outcome]}`,
      visit.variant ? ` · ${PLAYBOOK_VARIANT_LABELS[visit.variant]}` : '',
      visit.next_action ? ` · next: ${visit.next_action}` : '',
    ],
    href: lead ? `#/leads/${encodeURIComponent(lead.id)}` : undefined,
  });
};

export const activityView: View = ({ root }) => {
  shell.setChrome(true);
  const host = h('div', { class: 'page page--activity' });
  root.appendChild(host);
  let span: Span = 'today';
  let channel: TouchChannel | 'all' = 'all';
  let disposed = false;

  const filterChips = <T extends string>(
    options: readonly { value: T; label: string }[],
    current: () => T,
    onPick: (v: T) => void,
  ) => {
    const bar = h(
      'div',
      { class: 'chips chips--filters' },
      options.map((o) =>
        h(
          'button',
          {
            type: 'button',
            class: 'chip',
            'aria-pressed': String(o.value === current()),
            onClick: () => onPick(o.value),
          },
          o.label,
        ),
      ),
    );
    return bar;
  };

  const render = async () => {
    if (disposed) return;
    const today = todayLocal();
    const [snap, profile] = await Promise.all([getSnapshot(), loadExportProfile()]);
    if (disposed) return;
    const all = touchesOf(snap.visits, snap.leads);
    const shown = filterTouches(all, { from: fromFor(span, today), channel });
    shell.setTitle('Activity', {
      subtitle: `${dayHeading(today)} · ${shown.length} touch${shown.length === 1 ? '' : 'es'}`,
      back: '#/home',
      action: linkBtn('Fields', '#/activity/fields', { variant: 'subtle', size: 'sm' }),
    });
    replaceChildren(
      host,
      filterChips(SPANS, () => span, (v) => {
        span = v;
        void render();
      }),
      filterChips(
        [{ value: 'all' as const, label: 'All ways' }, ...TOUCH_CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABELS[c] }))],
        () => channel,
        (v) => {
          channel = v;
          void render();
        },
      ),
      section(
        { title: 'Touches', count: shown.length },
        shown.length ? shown.map(touchRow) : empty('Nothing logged in this stretch.'),
      ),
      exportSection(shown, profile),
    );
  };

  const exportSection = (touches: readonly Touch[], profile: ExportProfile) =>
    section(
      { title: 'Hand to Salesforce' },
      h('p', { class: 'card__summary' }, touchSummary(touches)),
      h(
        'p',
        { class: 'activity__note' },
        'Salesforce stays the system of record and nothing here talks to it. This is your log in its shape — paste the block or import the file yourself.',
      ),
      h(
        'div',
        { class: 'activity__actions' },
        btn('Copy the block', {
          variant: 'primary',
          disabled: touches.length === 0,
          onClick: () =>
            void copyText(touchesBlock(touches, profile)).then((ok) =>
              shell.toast(ok ? `${touches.length} copied` : 'Could not copy', {
                kind: ok ? 'success' : 'error',
              }),
            ),
        }),
        btn('Download CSV', {
          variant: 'subtle',
          disabled: touches.length === 0,
          onClick: () =>
            void exportFile(`activity-${todayLocal()}.csv`, touchesCsv(touches, profile)).catch(
              (err: unknown) =>
                shell.toast(`Could not save the file: ${err instanceof Error ? err.message : String(err)}`, {
                  kind: 'error',
                }),
            ),
        }),
      ),
    );

  void render();
  const off = onStoreChange(() => void render());
  return () => {
    disposed = true;
    off();
  };
};
